"use client";

import { ViewportTracking } from "@dafiti/analytics-sdk";
import type { ViewportTrackingData } from "@dafiti/analytics-sdk";
import { Button } from "@dafiti/closet-ui";
import { Chevron, Loading } from "@dafiti/closet-ui/icons";
import { useTranslations } from "next-intl";

import { useCatalogResults } from "../../../hooks/useCatalogResults.hook";
import {
  catalogPaginationClick,
  catalogView,
} from "../../../tracking/catalogPage.tracking";
import type {
  CatalogMetaResponse,
  CatalogProduct,
} from "../../../types/catalog.type";
import { CatalogBreadcrumb } from "../CatalogBreadcrumb/CatalogBreadcrumb";
import { CatalogPagination } from "../CatalogPagination";
import { CatalogGrid } from "./CatalogGrid";
import { CatalogGridSkeleton } from "./CatalogGridSkeleton";

type Props = {
  meta: CatalogMetaResponse;
  userAgent?: string | null;
  breadcrumb?: CatalogMetaResponse["breadcrumbs"];
};

export function CatalogResults({
  meta,
  userAgent,
  breadcrumb,
}: Readonly<Props>) {
  const t = useTranslations("catalog");
  const {
    products,
    isLoading,
    isLastPage,
    totalPages,
    loadMoreProducts,
    isFetchingMore,
  } = useCatalogResults({ userAgent });

  return (
    <main className="mb-xs lg:mb-12">
      {isLoading ? (
        <CatalogGridSkeleton />
      ) : (
        <ViewportTracking
          onTrack={(trackingData) =>
            catalogView({
              trackingData: trackingData.filter(
                (item): item is ViewportTrackingData<CatalogProduct> =>
                  item.data !== null && item.data !== undefined,
              ),
              catalogMeta: meta,
            })
          }
        >
          <CatalogGrid products={products} meta={meta} />
        </ViewportTracking>
      )}

      {isLastPage ? (
        <p className="text-center text-xs text-black pt-6 pb-1">
          {t("endOfList")}
        </p>
      ) : null}

      <div className="lg:hidden flex justify-center pt-2xs pb-3xs">
        {!isLastPage ? (
          <Button
            className="max-w-30 w-full flex px-2"
            variant="secondary"
            size="sm"
            disabled={isFetchingMore}
            onClick={() => {
              catalogPaginationClick();
              loadMoreProducts?.();
            }}
          >
            {isFetchingMore ? (
              <div className="w-6 h-6 flex items-center justify-center">
                <Loading className="size-4 text-black" />
              </div>
            ) : (
              <>
                <span className="ml-2">{t("loadMore")}</span>

                <div className="w-6 h-6 flex items-center justify-center ml-1">
                  <Chevron className="size-2 rotate-180 shrink-0" />
                </div>
              </>
            )}
          </Button>
        ) : null}
      </div>

      {breadcrumb?.length ? (
        <div className="lg:hidden mt-3xs">
          <CatalogBreadcrumb breadcrumb={breadcrumb} />
        </div>
      ) : null}

      <div className="hidden lg:block">
        <CatalogPagination totalPages={totalPages} />
      </div>
    </main>
  );
}
